import { memo, useState, useEffect } from 'react'
/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/react'
import moment from 'moment'
import withStyles from '@material-ui/core/styles/withStyles'
import CircularProgress from '@material-ui/core/CircularProgress'
import unionBackgroundImage from 'root/src/client/public/unionBackground.svg'
import Typography, { typographyVariants } from 'root/src/client/web/typography/Typography'
import {
	colorsNew,
	dropShadow,
	gtMdMediaQuery,
	ltGtMediaQuery,
} from 'root/src/client/web/commonStyles'
import useInterval from 'root/src/client/web/hooks/useInterval'
import { countdownColors } from 'root/src/shared/constants/dropEvent'

const getTimeDifferenceFromNow = date => moment.duration(Math.abs(moment().diff(moment(date))))

const liveCountdownStyles = {
	liveCountdown: {
		boxSizing: 'border-box',
		position: 'relative',
		display: 'flex',
		justifyContent: 'space-around',
		alignItems: 'center',
		backgroundImage: `url('${unionBackgroundImage.src}')`,
		backgroundSize: 'cover',
		backgroundPosition: 'center',
		backgroundRepeat: 'repeat',
		marginBottom: 30,
		height: 200,
		[gtMdMediaQuery]: {
			backgroundPosition: '24px center',
		},
		[ltGtMediaQuery]: {
			flexDirection: 'column',
			justifyContent: 'center',
			alignItems: 'center',
			height: 320,
			marginBottom: 0,
			backgroundImage: 'none',
			marginTop: 15,
		},
	},
	progress: {
		margin: [[0, 30]],
		[ltGtMediaQuery]: {
			margin: [[0, 10]],
			flexDirection: 'column',
		},
	},
	dropComingSoon: {
		zIndex: 1,
		textAlign: 'center',
	},
	liveCountdownContainer: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		transform: 'translateY(-10px)',
		[ltGtMediaQuery]: {
			flexWrap: 'wrap',
			flex: '1 1 50%',
			transform: 'translateY(0px)',
		},
	},
	liveCountdownImage: {
		position: 'absolute',
		left: 0,
		top: 0,
		[gtMdMediaQuery]: {
			left: 10,
		},
		height: '100%',
		width: '100%',
	},
	liveCountdownBadge: {
		transform: 'translate(0px, 40px)',
		[ltGtMediaQuery]: {
			transform: 'none',
			display: 'none',
		},
	},
	mobileText: {
		display: 'none',
		marginBottom: 25,
		filter: dropShadow(colorsNew.primary),
		[ltGtMediaQuery]: {
			display: 'flex',
		},
	},
}

const CircularProgressWithLabelStyles = {
	progressContainer: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		[ltGtMediaQuery]: {
			width: '50%',
			margin: [[10, 0]],
		},
	},
	progressWrapper: {
		position: 'relative',
		display: 'inline-flex',
	},
	circularProgress: {
		'-webkit-filter': 'drop-shadow( 3px 3px 2px rgba(0, 0, 0, .7))',
		filter: 'drop-shadow(3px 3px 2px rgba(0, 0, 0, .7))',
	},
	progressTextWrapper: {
		top: 0,
		left: 0,
		bottom: 0,
		right: 0,
		position: 'absolute',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
	progressLabel: {
		fontSize: 28,
	},
	progressSubLabel: {
		marginTop: 10,
		fontSize: 18,
	},
}
const CircularProgressWithLabelUnstyled = memo(({
	color, value, label, subLabel, classes, ...props
}) => {
	const [hydrated, setHydrated] = useState(false)
	useEffect(() => {
		setHydrated(true)
	}, [])
	if (!hydrated) {
		// it returns null on first render, so the client and server match
		// react hydration breaks without this
		return null
	}
	return (
		<div className={classes.progressContainer}>
			<div className={classes.progressWrapper}>
				<CircularProgress
					className={classes.circularProgress}
					css={{
						borderRadius: '100%',
						boxShadow: 'inset 0 0 0px 10px #FFF',
						backgroundColor: 'transparent',
						'& svg': {
							'-webkit-filter': 'drop-shadow(0px 0px 1px rgba(0,0,0,.6))',
							filter: 'drop-shadow(1px 1px 1px #000)',
						},
						'& svg circle': {
							borderRadius: 10,
							strokeLinecap: 'round',
							stroke: `${color} !important`,
						},
					}}
					thickness={5}
					color="primary"
					variant="determinate"
					value={value > 100 ? 100 : value}
					{...props}
				/>
				<div className={classes.progressTextWrapper}>
					<Typography
						variant={typographyVariants.paragraphBold}
						className={classes.progressLabel}
					>
						{label}
					</Typography>
				</div>
			</div>
			<Typography
				variant={typographyVariants.heading4}
				className={classes.progressSubLabel}
			>
				{subLabel}
			</Typography>
		</div>
	)
})

const CircularProgressWithLabel = withStyles(
	CircularProgressWithLabelStyles, { name: 'MuiCircularProgress' },
)(CircularProgressWithLabelUnstyled)

const COUNTDOWN_INTERVAL = 1000

const LiveCountdown = ({ classes, date, onFinish, headerText, bannerImage }) => {
	const [countdownTime, setCountdownTime] = useState(getTimeDifferenceFromNow(date))

	useInterval(async () => {
		const newTimeLeft = getTimeDifferenceFromNow(date)

		const shouldTriggerOnFinish = moment()
			.add(COUNTDOWN_INTERVAL, 'milliseconds')
			.isSameOrAfter(moment.utc(date), 'seconds')

		if (newTimeLeft >= 0) {
			setCountdownTime(newTimeLeft)
		}

		if (shouldTriggerOnFinish) {
			await onFinish()
		}
	}, COUNTDOWN_INTERVAL)

	return countdownTime ? (
		<div className={classes.liveCountdown}>
			<Typography
				className={classes.mobileText}
				variant={typographyVariants.heading1}
			>
				{headerText}
			</Typography>
			<div className={classes.liveCountdownContainer}>
				<CircularProgressWithLabel
					className={classes.progress}
					value={Math.floor(countdownTime.asDays()) / 365 * 100}
					color={countdownColors.days}
					size={90}
					label={Math.floor(countdownTime.asDays())}
					subLabel="DAYS"
				/>
				<CircularProgressWithLabel
					className={classes.progress}
					value={countdownTime.hours() / 24 * 100}
					color={countdownColors.hours}
					size={90}
					label={countdownTime.hours()}
					subLabel="HOURS"
				/>
				<div className={classes.liveCountdownBadge}>
					<img src={bannerImage} alt={headerText} />
				</div>
				<CircularProgressWithLabel
					className={classes.progress}
					value={countdownTime.minutes() / 60 * 100}
					color={countdownColors.minutes}
					size={90}
					label={countdownTime.minutes()}
					subLabel="MINUTES"
				/>
				<CircularProgressWithLabel
					className={classes.progress}
					color={countdownColors.seconds}
					size={90}
					value={countdownTime.seconds() / 60 * 100}
					label={countdownTime.seconds()}
					subLabel="SECONDS"
				/>
			</div>
		</div>
	) : null
}

export default withStyles(liveCountdownStyles, { name: 'MuiLiveCountdown' })(memo(LiveCountdown))
